/**
 * Shared currency-amount formatter used across proposal views.
 *
 * Design:
 *  - Symbols are hand-curated so every currency gets a clear, unambiguous
 *    prefix — especially USD, which shows as "US$" here (browser default is
 *    just "$"). This matters on proposals where CAD venues and USD venues
 *    can sit side-by-side.
 *  - Numbers use en-US grouping for consistency with the rest of the app.
 *  - Spacing: letter-terminated symbols (e.g. "CHF") get a separating space;
 *    punctuation-terminated symbols (e.g. "US$", "€") sit flush against
 *    the number.
 */

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: 'US$',
  CAD: 'CA$',
  EUR: '€',
  GBP: '£',
  AUD: 'A$',
  CHF: 'CHF',
};

export interface FormatCurrencyOptions {
  /** Number of fraction digits. Defaults to 0. */
  fractionDigits?: number;
  /** Value to return when amount is null/undefined/NaN. Defaults to "—". */
  fallback?: string;
}

/**
 * Format a numeric amount with its currency symbol.
 *
 * Examples:
 *   formatCurrency(1234, 'USD')  → "US$1,234"
 *   formatCurrency(1234, 'CAD')  → "CA$1,234"
 *   formatCurrency(1234, 'EUR')  → "€1,234"
 *   formatCurrency(1234, 'CHF')  → "CHF 1,234"
 *   formatCurrency(1234, 'ZZZ')  → "ZZZ 1,234"  (unknown code → use as-is)
 *   formatCurrency(null, 'USD')  → "—"
 */
export function formatCurrency(
  amount: number | null | undefined,
  currencyCode: string = 'USD',
  options: FormatCurrencyOptions = {},
): string {
  const { fractionDigits = 0, fallback = '—' } = options;

  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return fallback;
  }

  const number = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount);

  const symbol = CURRENCY_SYMBOLS[currencyCode] ?? currencyCode;
  const needsSpace = /[A-Za-z]$/.test(symbol);
  return needsSpace ? `${symbol} ${number}` : `${symbol}${number}`;
}
